import React, { useEffect, useMemo, useState } from "react";
import Header from "../general/Header";
import Aside from "../general/Aside";
import Footer from "../general/Footer";
import axios from '../../axios/axiosconf';
import Loading from "../general/Loading";
import Unauthorized from "../general/Unauthorized";
import { Link } from "react-router-dom";
import { descargarCsvReporte, descargarExcelXlsx } from '../../../utils/reporteExportHelpers';

const FORMA_PAGO_MAP = {
    '10': 'Efectivo',
    '42': 'Consignación bancaria',
    '1': 'Instrumento no definido',
    '20': 'Cheque',
    '30': 'Transferencia Crédito',
    '31': 'Transferencia Débito',
    '45': 'Transferencia Crédito Bancario',
    '47': 'Transferencia Débito Bancaria',
    '48': 'Tarjeta Crédito',
    '49': 'Tarjeta Débito',
    ZZZ: 'Acuerdo mutuo',
};

function tienePermisoReporte(userdata) {
    if (userdata.permissions && Array.isArray(userdata.permissions)) {
        return userdata.permissions.some((p) =>
            p.slug === 'ver_listado_presupuestos'
            || p.slug === 'ver_listado_facturas'
            || p.slug === 'verReportes'
        );
    }
    const up = userdata.up || [];
    return up.includes('ver_listado_presupuestos')
        || up.includes('ver_listado_facturas')
        || up.includes('verReportes');
}

function ReportePresupuestosAprobados() {
    const [loggedIn, setLoggedIn] = useState(false);
    const [hasPermission, setHasPermission] = useState(false);
    const [client, setClient] = useState([]);
    const [loading, setLoading] = useState(false);
    const [presupuestos, setPresupuestos] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [pacientes, setPacientes] = useState([]);

    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [usuarioFiltro, setUsuarioFiltro] = useState('');
    const [busquedaPaciente, setBusquedaPaciente] = useState('');
    const [busquedaDocumento, setBusquedaDocumento] = useState('');
    const [estadoFiltro, setEstadoFiltro] = useState('');
    const [historicoCompleto, setHistoricoCompleto] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem('userdata')) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }
        const userdata = JSON.parse(localStorage.getItem('userdata'));
        if (!tienePermisoReporte(userdata)) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }
        setHasPermission(true);

        const hoy = new Date();
        const inicioSemana = new Date(hoy);
        inicioSemana.setDate(hoy.getDate() - hoy.getDay());
        const finSemana = new Date(inicioSemana);
        finSemana.setDate(inicioSemana.getDate() + 6);
        setFechaInicio(inicioSemana.toISOString().split('T')[0]);
        setFechaFin(finSemana.toISOString().split('T')[0]);

        axios.get('/get-client?uid=' + userdata.user_id)
            .then((response) => {
                if (response.status === 200) setClient(response.data);
                setLoggedIn(true);
            })
            .catch(() => setLoggedIn(true));

        axios.get('/users-by-client?uid=' + userdata.user_id)
            .then((response) => {
                if (response.status === 200) setUsuarios(response.data);
            })
            .catch(() => setUsuarios([]));

        Promise.all([
            axios.get('/pacientes', { params: { uid: userdata.user_id } }),
            axios.get('/pacientes', { params: { uid: userdata.user_id, solo_empresas: true } }),
        ])
            .then(([respPacientes, respEmpresas]) => {
                const listaP = Array.isArray(respPacientes.data) ? respPacientes.data : [];
                const listaE = Array.isArray(respEmpresas.data) ? respEmpresas.data : [];
                const unicos = Array.from(new Map([...listaP, ...listaE].map((item) => [item.id, item])).values());
                setPacientes(unicos);
            })
            .catch(() => setPacientes([]));
    }, []);

    useEffect(() => {
        if (loggedIn && (historicoCompleto || (fechaInicio && fechaFin))) {
            cargarReporte();
        }
    }, [loggedIn, historicoCompleto, fechaInicio, fechaFin, usuarioFiltro, estadoFiltro, busquedaPaciente, busquedaDocumento]);

    const cargarReporte = () => {
        if (!historicoCompleto && (!fechaInicio || !fechaFin)) {
            alert('Seleccione fecha inicio y fecha fin, o active "Histórico completo".');
            return;
        }
        setLoading(true);
        const userdata = JSON.parse(localStorage.getItem('userdata'));
        const fechaInicioConsulta = historicoCompleto ? '1990-01-01' : fechaInicio;
        const fechaFinConsulta = historicoCompleto ? '3000-12-31' : fechaFin;

        const params = new URLSearchParams({
            uid: userdata.user_id,
            fecha_inicio: fechaInicioConsulta,
            fecha_fin: fechaFinConsulta,
        });

        if (usuarioFiltro) params.append('usuario_id', usuarioFiltro);
        if (busquedaPaciente) params.append('busqueda_paciente', busquedaPaciente);
        if (busquedaDocumento) params.append('busqueda_documento', busquedaDocumento);

        if (estadoFiltro !== '') {
            params.append('estado', estadoFiltro);
        } else {
            params.append('estados', '1,2,3');
        }

        axios.get('/presupuestos?' + params.toString())
            .then((response) => {
                setPresupuestos(Array.isArray(response.data) ? response.data : []);
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setPresupuestos([]);
                setLoading(false);
            });
    };

    const handleLimpiarFiltros = () => {
        const hoy = new Date();
        const inicioSemana = new Date(hoy);
        inicioSemana.setDate(hoy.getDate() - hoy.getDay());
        const finSemana = new Date(inicioSemana);
        finSemana.setDate(inicioSemana.getDate() + 6);
        setFechaInicio(inicioSemana.toISOString().split('T')[0]);
        setFechaFin(finSemana.toISOString().split('T')[0]);
        setUsuarioFiltro('');
        setBusquedaPaciente('');
        setBusquedaDocumento('');
        setEstadoFiltro('');
        setHistoricoCompleto(false);
        setTimeout(() => cargarReporte(), 100);
    };

    const getPaciente = (pacienteId) => pacientes.find((p) => p.id == pacienteId);

    const getNombreMostrarCliente = (p) => {
        if (!p) return '';
        const nombrePersona = `${p.primer_nombre || ''} ${p.segundo_nombre || ''} ${p.primer_apellido || ''} ${p.segundo_apellido || ''}`.trim();
        return nombrePersona || p.razon_social || p.nombre_empresa || p.nombre_comercial || p.nombre || '';
    };

    const getNombrePaciente = (pacienteId) => {
        const p = getPaciente(pacienteId);
        const s = getNombreMostrarCliente(p).trim();
        return s || 'N/A';
    };

    const getDocumentoPaciente = (pacienteId) => getPaciente(pacienteId)?.numero_documento || 'N/A';

    const getContactoPaciente = (pacienteId) => {
        const p = getPaciente(pacienteId);
        return p?.celular || p?.telefono || 'N/A';
    };

    const getEmailPaciente = (pacienteId) => getPaciente(pacienteId)?.email || 'N/A';

    const getNombreUsuario = (usuarioId) => {
        const u = usuarios.find((x) => x.id == usuarioId);
        return u ? (u.name || '') : `Usuario ${usuarioId}`;
    };

    const getFormaPagoTexto = (codigo) => {
        if (codigo == null || codigo === '') return 'N/A';
        return FORMA_PAGO_MAP[String(codigo).trim()] || codigo;
    };

    const getEstadoPresupuestoTexto = (estado) => {
        switch (parseInt(estado, 10)) {
            case 1: return 'Activo';
            case 2: return 'Anulado';
            case 3: return 'Pagado';
            default: return 'Otro';
        }
    };

    const getEstadoChipClass = (estado) => {
        const e = parseInt(estado, 10);
        if (e === 1) return 'report-chip report-chip--warn';
        if (e === 2) return 'report-chip report-chip--bad';
        if (e === 3) return 'report-chip report-chip--ok';
        return 'report-chip report-chip--neutral';
    };

    const totalesResumen = useMemo(() => {
        let totalValor = 0;
        let totalPagado = 0;
        let totalPendiente = 0;
        let activos = 0;
        let anulados = 0;
        let pagados = 0;
        for (const p of presupuestos) {
            const est = parseInt(p.estado, 10);
            const vt = parseFloat(p.valor_total || 0);
            const pag = parseFloat(p.valor_consumido || 0);
            const pend = parseFloat(p.saldo_disponible || 0);
            totalValor += vt;
            totalPagado += pag;
            if (est === 1) {
                activos++;
                totalPendiente += pend;
            } else if (est === 2) anulados++;
            else if (est === 3) pagados++;
        }
        return {
            recuento: presupuestos.length,
            activos,
            anulados,
            pagados,
            total_valor: totalValor,
            total_pagado: totalPagado,
            total_pendiente: totalPendiente,
        };
    }, [presupuestos]);

    const formatearMonedaResumen = (valor) =>
        '$' + Number(valor || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const valorEntero = (valor) => Math.round(Number(valor || 0));

    const filasExcel = () => presupuestos.map((p) => ({
        'N° Presupuesto': p.numero_presupuesto || p.id,
        'Fecha': p.fecha_creacion ? new Date(p.fecha_creacion).toLocaleDateString('es-CO') : '',
        'Estado': getEstadoPresupuestoTexto(p.estado),
        'Paciente': getNombrePaciente(p.paciente_id),
        'Documento': getDocumentoPaciente(p.paciente_id),
        'Contacto': getContactoPaciente(p.paciente_id),
        'Correo': getEmailPaciente(p.paciente_id),
        'Profesional': getNombreUsuario(p.users_id),
        'Forma pago': getFormaPagoTexto(p.forma_pago),
        'Valor bruto': valorEntero(p.valor_bruto),
        'Total': valorEntero(p.valor_total),
        'Pagado': valorEntero(p.valor_consumido),
        'Pendiente': valorEntero(p.saldo_disponible),
    }));

    const exportarExcel = () => {
        if (presupuestos.length === 0) {
            alert('No hay datos para exportar');
            return;
        }
        descargarExcelXlsx(
            filasExcel(),
            'Presupuestos',
            `reporte_presupuestos_aprobados_${new Date().toISOString().split('T')[0]}.xlsx`
        );
    };

    const exportarCSV = () => {
        if (presupuestos.length === 0) {
            alert('No hay datos para exportar');
            return;
        }
        descargarCsvReporte(
            filasExcel(),
            `reporte_presupuestos_aprobados_${new Date().toISOString().split('T')[0]}.csv`
        );
    };

    if (hasPermission === false) return <Unauthorized />;
    if (!loggedIn) return <Loading />;

    return (
        <div className="hold-transition" style={{ fontSize: '13px' }}>
            <Header client={client} />
            <Aside client={client} activelink="reports" />
            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-12">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas">Reportes</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas-contables">Contables</Link></li>
                                    <li className="breadcrumb-item active">Presupuestos aprobados</li>
                                </ol>
                            </div>
                        </div>
                        <h1 className="m-0">Reporte de Presupuestos  Aprobados</h1>
                        
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="card report-fin-card">
                            <div className="card-header">
                                <h3 className="card-title">Filtros</h3>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-2">
                                        <label>Fecha inicio</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={fechaInicio}
                                            disabled={historicoCompleto}
                                            onChange={(e) => setFechaInicio(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label>Fecha fin</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={fechaFin}
                                            disabled={historicoCompleto}
                                            onChange={(e) => setFechaFin(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label>Profesional</label>
                                        <select className="form-control form-control-sm" value={usuarioFiltro} onChange={(e) => setUsuarioFiltro(e.target.value)}>
                                            <option value="">Todos</option>
                                            {usuarios.map((u) => (
                                                <option key={u.id} value={u.id}>{u.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label>Estado</label>
                                        <select className="form-control form-control-sm" value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)}>
                                            <option value="">Todos</option>
                                            <option value="1">Activos</option>
                                            <option value="2">Anulados</option>
                                            <option value="3">Pagados</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label className="d-block">Opciones</label>
                                        <div className="form-check mt-1">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="historicoCompletoPres"
                                                checked={historicoCompleto}
                                                onChange={(e) => setHistoricoCompleto(e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="historicoCompletoPres">Histórico completo</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-4">
                                        <label>Paciente</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Nombre..."
                                            value={busquedaPaciente}
                                            onChange={(e) => setBusquedaPaciente(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label>Identificación</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Documento..."
                                            value={busquedaDocumento}
                                            onChange={(e) => setBusquedaDocumento(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-4 d-flex align-items-end">
                                        <button type="button" className="btn btn-primary btn-sm mr-2" onClick={cargarReporte} disabled={loading}>
                                            <i className="fas fa-search" /> Buscar
                                            {loading && <span className="spinner-border spinner-border-sm ml-2" />}
                                        </button>
                                        <button type="button" className="btn btn-secondary btn-sm mr-2" onClick={handleLimpiarFiltros}>
                                            <i className="fas fa-eraser" /> Limpiar
                                        </button>
                                        <button type="button" className="btn btn-success btn-sm mr-2" onClick={exportarExcel} disabled={presupuestos.length === 0}>
                                            <i className="fas fa-file-excel" /> Excel
                                        </button>
                                        <button type="button" className="btn btn-info btn-sm" onClick={exportarCSV} disabled={presupuestos.length === 0}>
                                            <i className="fas fa-file-csv" /> CSV
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-3">
                                <div className="card card-outline card-secondary">
                                    <div className="card-body">
                                        <h3 className="mb-0">{totalesResumen.recuento}</h3>
                                        <p className="text-muted mb-0 small">Total registros</p>
                                        <p className="mb-0 small text-muted">Act.: {totalesResumen.activos} · Anul.: {totalesResumen.anulados} · Pag.: {totalesResumen.pagados}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-secondary">
                                    <div className="card-body">
                                        <h3 className="mb-0">{formatearMonedaResumen(totalesResumen.total_valor)}</h3>
                                        <p className="text-muted mb-0 small">Suma valor total</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-success">
                                    <div className="card-body">
                                        <h3 className="mb-0">{formatearMonedaResumen(totalesResumen.total_pagado)}</h3>
                                        <p className="text-muted mb-0 small">Suma pagado / consumido</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-warning">
                                    <div className="card-body">
                                        <h3 className="mb-0">{formatearMonedaResumen(totalesResumen.total_pendiente)}</h3>
                                        <p className="text-muted mb-0 small">Pendiente (activos)</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card report-fin-card mt-3">
                            <div className="card-header">
                                <h3 className="card-title">Resultados ({presupuestos.length})</h3>
                            </div>
                            <div className="card-body p-0">
                                {loading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border" role="status"><span className="sr-only">Cargando...</span></div>
                                    </div>
                                ) : (
                                    <div className="table-responsive" style={{ overflowX: 'auto' }}>
                                        <table className="table table-bordered table-hover mb-0 report-fin-table" style={{ minWidth: '1400px' }}>
                                            <thead>
                                                <tr>
                                                    <th className="text-center">N° Presupuesto</th>
                                                    <th className="text-center">Fecha</th>
                                                    <th className="text-center">Estado</th>
                                                    <th className="text-center">Paciente</th>
                                                    <th className="text-center">Documento</th>
                                                    <th className="text-center">Contacto</th>
                                                    <th className="text-center">Correo</th>
                                                    <th className="text-center">Profesional</th>
                                                    <th className="text-center">Forma pago</th>
                                                    <th className="text-center">Valor bruto</th>
                                                    <th className="text-center">Total</th>
                                                    <th className="text-center">Pagado</th>
                                                    <th className="text-center">Pendiente</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {presupuestos.length > 0 ? presupuestos.map((p) => (
                                                    <tr key={p.id}>
                                                        <td className="text-center"><strong>{p.numero_presupuesto || `#${p.id}`}</strong></td>
                                                        <td className="text-center">{p.fecha_creacion ? new Date(p.fecha_creacion).toLocaleDateString('es-CO') : ''}</td>
                                                        <td className="text-center">
                                                            <span className={getEstadoChipClass(p.estado)}>{getEstadoPresupuestoTexto(p.estado)}</span>
                                                        </td>
                                                        <td>{getNombrePaciente(p.paciente_id)}</td>
                                                        <td className="text-center">{getDocumentoPaciente(p.paciente_id)}</td>
                                                        <td className="text-center">{getContactoPaciente(p.paciente_id)}</td>
                                                        <td style={{ wordBreak: 'break-all' }}>{getEmailPaciente(p.paciente_id)}</td>
                                                        <td>{getNombreUsuario(p.users_id)}</td>
                                                        <td>{getFormaPagoTexto(p.forma_pago)}</td>
                                                        <td className="text-right">{formatearMonedaResumen(p.valor_bruto)}</td>
                                                        <td className="text-right"><strong>{formatearMonedaResumen(p.valor_total)}</strong></td>
                                                        <td className="text-right">{formatearMonedaResumen(p.valor_consumido)}</td>
                                                        <td className="text-right">{formatearMonedaResumen(p.saldo_disponible)}</td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="13" className="text-center py-4 text-muted">No hay presupuestos con los filtros aplicados</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <Footer />
        </div>
    );
}

export default ReportePresupuestosAprobados;
